/**
 * Admin Route Guard
 *
 * Restricts route access to users with admin privileges.
 * Awaits viewer data (which may not be loaded yet on page refresh)
 * before checking permissions via the typed `canManageSomething()` API.
 *
 * History: this guard previously used `canDo('*') || canDo('admin:*')`
 * which only matches users with the LITERAL `'*'` or `'admin:*'` action.
 * Real admin users don't hold those — they hold `customer:manage`,
 * `space:manage`, etc. The legacy `isAdmin` was a permissive substring
 * scan (matched `*admin*` / `*manage*` / `facility:analytics` in any
 * action). `canManageSomething()` replaces both with a concrete predicate
 * on admin-class action shapes.
 *
 * Per-route action mapping is the strictly-correct long-term shape; this
 * coarse gate is a deliberate compromise so rail visibility, route guard,
 * and profile-panel "Admin" entry all answer the same question.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { usesBearerTokenAuth } from '../auth-mode';
import { ProfileDataService } from '../services/profile-data.service';
import { SessionService } from '../services/session.service';
import { TeamsService } from '../services/teams.service';
import { ViewerService } from '../services/viewer.service';
import { isAdminPreview } from './admin-preview';
import { tryRefreshSession } from './auth.guard';
import { regionGateActive } from '../region';
import { HOME_ROUTE } from '../utils/home-link';

export const adminGuard: CanActivateFn = async (route) => {
  // Data-residency gate (desktop): no backend calls until a region is picked.
  if (regionGateActive()) return false;

  // Dev-only offline preview of the admin shell (see admin-preview.ts).
  if (isAdminPreview(route)) return true;

  const viewerService = inject(ViewerService);
  const profileData = inject(ProfileDataService);
  const session = inject(SessionService);
  const teamsService = inject(TeamsService);
  const router = inject(Router);
  await viewerService.ensureLoaded();
  let allowed = profileData.canManageSomething();

  // The viewer is cached for the session. If it was loaded with an expired
  // access token the viewer query fails silently and we cache a null/partial
  // viewer — so a user whose token expired while sitting in /admin would be
  // ejected to /home with no refresh attempt. Before denying, try the same
  // refresh path authGuard uses, force a fresh viewer load, and re-check.
  // This only runs on the would-be-denied path, so it can never regress the
  // previous behaviour (which bounced unconditionally).
  if (!allowed) {
    if (usesBearerTokenAuth(teamsService.isInTeams())) {
      await tryRefreshSession();
    } else {
      await session.refreshAccessToken();
    }
    await viewerService.reload();
    allowed = profileData.canManageSomething();
  }

  if (!allowed) {
    const actions = profileData.actionsSnapshot();
    // eslint-disable-next-line no-console
    console.warn(
      '[adminGuard] DENIED — viewer holds no admin-class action.',
      {
        actionsCount: actions.length,
        sampleActions: actions.slice(0, 15),
        canDoStar: profileData.canDo('*'),
        canDoAdminStar: profileData.canDo('admin:*'),
      },
    );
    // Redirect to the default home (the redesign) rather than silently 404
    // so the URL change is visible (matching the symptom Nathan reported).
    void router.navigate([HOME_ROUTE]);
  } else if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.debug('[adminGuard] ALLOWED — canManageSomething() = true');
  }
  return allowed;
};
